// src/pages/admin/AdminOrdersManagement.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  FiShoppingBag,
  FiSearch,
  FiDownload,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiDollarSign,
  FiCalendar,
  FiPackage,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
  FiPhone
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../../services/api";

const safeLower = (v) => String(v ?? "").toLowerCase();

const readTotalFromResponse = (res) => {
  try {
    const payload = res?.data ?? {};

    if (payload.totalItems ?? payload.total) return Number(payload.totalItems ?? payload.total);
    if (payload.meta && (payload.meta.total ?? payload.meta.totalItems)) return Number(payload.meta.total ?? payload.meta.totalItems);
    if (payload.pagination && (payload.pagination.total ?? payload.pagination.totalItems)) return Number(payload.pagination.total ?? payload.pagination.totalItems);

    // si payload.data est un objet pagination
    if (payload.data && !Array.isArray(payload.data)) {
      if (payload.data.total ?? payload.data.totalItems) return Number(payload.data.total ?? payload.data.totalItems);
    }

    // header
    const headerTotal = res?.headers?.["x-total-count"] || res?.headers?.["X-Total-Count"];
    if (headerTotal) return Number(headerTotal);
  } catch {}
  return 0;
};

const generateCSV = (data) => {
  const headers = ["Numéro", "Client", "Email", "Téléphone", "Total", "Statut paiement", "Statut commande", "Date"];
  const rows = data.map((order) => {
    const num = order.orderNumber || (order._id ? order._id.slice(-6) : "N/A");
    const client = `${order.userId?.firstName || ""} ${order.userId?.lastName || ""}`.trim() || "N/A";
    const email = order.userId?.email || "N/A";
    const phone = order.userId?.phone || "";
    const total = typeof order.total === "number" ? `${order.total} MAD` : order.total || "0 MAD";
    const paymentStatus = order.paymentStatus || "N/A";
    const status = order.status || "N/A";
    const date = order.createdAt ? new Date(order.createdAt).toLocaleDateString("fr-FR") : "";
    return [num, client, email, phone, total, paymentStatus, status, date];
  });

  const escapeCell = (cell) => {
    const s = String(cell ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  return [headers, ...rows].map((r) => r.map(escapeCell).join(",")).join("\n");
};

const downloadCSV = (content, filename) => {
  try {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 800);
  } catch {
    toast.error("Impossible de télécharger le CSV");
  }
};

// ---- date helpers (front) ----
const isSameDay = (d1, d2) => {
  try {
    const a = new Date(d1);
    const b = new Date(d2);
    return a.toDateString() === b.toDateString();
  } catch {
    return false;
  }
};

const isWithinLastDays = (date, days) => {
  try {
    const d = new Date(date).getTime();
    if (!Number.isFinite(d)) return false;
    const now = Date.now();
    const from = now - days * 24 * 60 * 60 * 1000;
    return d >= from && d <= now;
  } catch {
    return false;
  }
};

export default function AdminOrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // paymentStatus
  const [dateFilter, setDateFilter] = useState("all"); // today/week/month

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const searchDebounce = useRef(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(searchDebounce.current);
      try {
        abortRef.current?.abort();
      } catch {}
    };
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      completed: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800",
      refunded: "bg-red-100 text-red-800",
      processing: "bg-blue-100 text-blue-800"
    };
    const labels = {
      completed: "Payée",
      pending: "En attente",
      cancelled: "Annulée",
      refunded: "Remboursée",
      processing: "En cours"
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || "bg-gray-100 text-gray-800"}`}>
        {labels[status] || status || "—"}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <FiCheckCircle className="text-green-500" />;
      case "pending":
        return <FiClock className="text-yellow-500" />;
      case "cancelled":
      case "refunded":
        return <FiXCircle className="text-red-500" />;
      case "processing":
        return <FiPackage className="text-blue-500" />;
      default:
        return <FiClock className="text-gray-500" />;
    }
  };

  const fetchOrders = useCallback(
    async (p = page, l = limit, q = searchTerm, status = statusFilter, date = dateFilter) => {
      if (mountedRef.current) setLoading(true);

      try {
        try {
          abortRef.current?.abort();
        } catch {}
        abortRef.current = new AbortController();

        const params = { page: p, limit: l };
        if (q?.trim()) params.q = q.trim();
        if (status && status !== "all") params.paymentStatus = status;
        if (date && date !== "all") params.date = date;

        const res = await API.get("/admin/orders", {
          params,
          signal: abortRef.current.signal
        });

        const payload = res?.data ?? {};
        const list =
          Array.isArray(payload.data) ? payload.data :
          Array.isArray(payload.orders) ? payload.orders :
          Array.isArray(payload.items) ? payload.items :
          [];

        if (mountedRef.current) {
          setOrders(list);
          setTotal(readTotalFromResponse(res) || payload.total || payload.totalItems || list.length || 0);
        }
      } catch (err) {
        const isAbort =
          err?.name === "AbortError" ||
          err?.code === "ERR_CANCELED" ||
          err?.message === "canceled";

        if (isAbort) return;

        console.error("fetchOrders error:", err);
        toast.error("Erreur lors du chargement des commandes");

        if (mountedRef.current) {
          setOrders([]);
          setTotal(0);
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [page, limit, searchTerm, statusFilter, dateFilter]
  );

  useEffect(() => {
    fetchOrders(page, limit, searchTerm, statusFilter, dateFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPage(1);
      fetchOrders(1, limit, searchTerm, statusFilter, dateFilter);
    }, 350);

    return () => clearTimeout(searchDebounce.current);
  }, [searchTerm, statusFilter, dateFilter, fetchOrders, limit]);

  const handleStatusChange = async (orderId, payload) => {
    if (!orderId || !payload) return;

    setIsSubmitting(true);
    try {
      await API.put(`/admin/orders/${orderId}/status`, payload);
      toast.success("Statut mis à jour");
      await fetchOrders(page, limit, searchTerm, statusFilter, dateFilter);
    } catch (e) {
      console.error("handleStatusChange error:", e);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  // ---- client-side filtering (en plus du backend) ----
  const filteredOrders = useMemo(() => {
    let list = Array.isArray(orders) ? orders : [];

    const term = safeLower(searchTerm).trim();
    if (term) {
      list = list.filter((o) => {
        const num = safeLower(o.orderNumber || o._id || "");
        const client = safeLower(`${o.userId?.firstName || ""} ${o.userId?.lastName || ""}`.trim());
        const email = safeLower(o.userId?.email || "");
        const phone = safeLower(o.userId?.phone || "");
        return num.includes(term) || client.includes(term) || email.includes(term) || phone.includes(term);
      });
    }

    if (statusFilter && statusFilter !== "all") {
      list = list.filter((o) => safeLower(o.paymentStatus) === safeLower(statusFilter));
    }

    if (dateFilter && dateFilter !== "all") {
      const now = new Date();
      if (dateFilter === "today") {
        list = list.filter((o) => o.createdAt && isSameDay(o.createdAt, now));
      } else if (dateFilter === "week") {
        list = list.filter((o) => o.createdAt && isWithinLastDays(o.createdAt, 7));
      } else if (dateFilter === "month") {
        list = list.filter((o) => o.createdAt && isWithinLastDays(o.createdAt, 30));
      }
    }

    return list;
  }, [orders, searchTerm, statusFilter, dateFilter]);

  const stats = useMemo(() => {
    const all = Array.isArray(orders) ? orders : [];
    const totalItems = all.length;

    const todayCount = all.filter((o) => o.createdAt && isSameDay(o.createdAt, new Date())).length;

    const completed = all.filter((o) => o.paymentStatus === "completed");
    const revenue = completed.reduce((s, o) => s + (Number(o.total) || 0), 0);
    const average = completed.length ? revenue / completed.length : 0;

    const pending = all.filter((o) => o.paymentStatus === "pending").length;

    return {
      total: totalItems,
      today: todayCount,
      revenue,
      average,
      completed: completed.length,
      pending
    };
  }, [orders]);

  const totalPages = Math.max(1, Math.ceil((total || filteredOrders.length) / (limit || 1)));
  const goToPage = (p) => setPage(Math.max(1, Math.min(totalPages, p)));

  const handleBulkExport = () => {
    downloadCSV(generateCSV(filteredOrders), "orders-export.csv");
    toast.success("Export réussi !");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header + filtres */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Commandes</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Suivez et gérez toutes les commandes clients</p>
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleBulkExport}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              <FiDownload className="mr-2" />
              <span className="hidden sm:inline">Exporter CSV</span>
              <span className="sm:hidden">CSV</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="completed">Payées</option>
              <option value="pending">En attente</option>
              <option value="processing">En cours</option>
              <option value="cancelled">Annulées</option>
              <option value="refunded">Remboursées</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd&apos;hui</option>
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cards stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">Total</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FiShoppingBag className="text-blue-500 text-xl sm:text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">Aujourd&apos;hui</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.today}</p>
            </div>
            <FiCalendar className="text-purple-500 text-xl sm:text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">Revenu</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{stats.revenue.toLocaleString()} MAD</p>
            </div>
            <FiDollarSign className="text-green-500 text-xl sm:text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">Moyenne</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{Number(stats.average || 0).toFixed(0)} MAD</p>
            </div>
            <FiTrendingUp className="text-orange-500 text-xl sm:text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">Complétées</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <FiCheckCircle className="text-green-500 text-xl sm:text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">En Attente</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <FiClock className="text-yellow-500 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>

      {/* Tableau commandes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deals</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(order.paymentStatus)}
                        <span className="ml-2 text-xs sm:text-sm font-medium text-gray-900">
                          #{order.orderNumber || (order._id ? order._id.slice(-6) : "N/A")}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1 hidden sm:block">ID: {order._id}</div>
                    </td>

                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900">
                          {order.userId?.firstName || ""} {order.userId?.lastName || ""}
                        </div>
                        <div className="text-xs text-gray-500">{order.userId?.email}</div>
                        {order.userId?.phone && (
                          <div className="text-xs text-gray-500 flex items-center mt-1">
                            <FiPhone className="mr-1" />
                            {order.userId.phone}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className="text-xs sm:text-sm text-gray-900">{order.items?.length || 0} deal(s)</span>
                    </td>

                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className="text-xs sm:text-sm font-semibold text-gray-900">{order.total || 0} MAD</span>
                    </td>

                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">{getStatusBadge(order.paymentStatus)}</td>

                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString("fr-FR") : "-"}
                    </td>

                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowDetailsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Voir détails"
                        >
                          <FiEye size={18} />
                        </button>

                        {order.paymentStatus === "pending" && (
                          <button
                            type="button"
                            onClick={() =>
                              handleStatusChange(order._id, {
                                status: "delivered",
                                paymentStatus: "completed",
                                paymentMethod: (order.paymentMethod || order.payment_method || "").toLowerCase() || "cash"
                              })
                            }
                            className="text-green-600 hover:text-green-900"
                            title="Marquer comme payée"
                            disabled={isSubmitting}
                          >
                            <FiCheckCircle size={18} />
                          </button>
                        )}

                        {order.paymentStatus !== "refunded" && order.paymentStatus !== "cancelled" && (
                          <button
                            type="button"
                            onClick={() =>
                              handleStatusChange(order._id, {
                                status: "cancelled",
                                paymentStatus: "refunded"
                              })
                            }
                            className="text-red-600 hover:text-red-900"
                            title="Rembourser / annuler"
                            disabled={isSubmitting}
                          >
                            <FiXCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <FiShoppingBag className="mx-auto text-4xl mb-2" />
                    <p>Aucune commande trouvée</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t bg-white gap-3">
          <div className="flex items-center justify-center sm:justify-start space-x-2">
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1 text-sm"
            >
              <FiChevronLeft /> <span>Préc</span>
            </button>

            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1 text-sm"
            >
              <span>Suiv</span> <FiChevronRight />
            </button>

            <div className="ml-4 text-xs sm:text-sm text-gray-600">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </div>
          </div>

          <div className="flex items-center justify-center sm:justify-end gap-3 text-xs sm:text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>Afficher</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="border px-2 py-1 rounded text-xs sm:text-sm"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>/ page</span>
            </div>

            <div>
              {total > 0 ? `Total: ${total}` : `${filteredOrders.length} affichés`}
            </div>
          </div>
        </div>
      </div>

      {/* Modal détails - VERSION RESPONSIVE */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl font-bold">Détails de la commande</h2>
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Fermer"
                >
                  <FiXCircle size={24} />
                </button>
              </div>

              {/* Boutons admin - Responsive */}
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                {selectedOrder.paymentStatus === "pending" && (
                  <button
                    type="button"
                    onClick={() =>
                      handleStatusChange(selectedOrder._id, {
                        status: "delivered",
                        paymentStatus: "completed",
                        paymentMethod:
                          (selectedOrder.paymentMethod || selectedOrder.payment_method || "").toLowerCase() || "cash"
                      })
                    }
                    className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    disabled={isSubmitting}
                  >
                    <FiCheckCircle className="mr-2" />
                    Confirmer la commande
                  </button>
                )}

                {selectedOrder.paymentStatus !== "refunded" && selectedOrder.paymentStatus !== "cancelled" && (
                  <button
                    type="button"
                    onClick={() =>
                      handleStatusChange(selectedOrder._id, {
                        status: "cancelled",
                        paymentStatus: "refunded"
                      })
                    }
                    className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    disabled={isSubmitting}
                  >
                    <FiXCircle className="mr-2" />
                    Rembourser
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {/* Infos générales - RESPONSIVE */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm sm:text-base">Informations commande</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="break-words">
                      <span className="text-gray-600">Numéro:</span>
                      <span className="ml-2 font-medium break-all">
                        #{selectedOrder.orderNumber || (selectedOrder._id ? selectedOrder._id.slice(-6) : "")}
                      </span>
                    </div>

                    <div className="break-words">
                      <span className="text-gray-600">ID:</span>
                      <span className="ml-2 text-xs break-all">{selectedOrder._id}</span>
                    </div>

                    <div>
                      <span className="text-gray-600">Date:</span>
                      <span className="ml-2">
                        {selectedOrder.createdAt
                          ? new Date(selectedOrder.createdAt).toLocaleString("fr-FR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit"
                            })
                          : "-"}
                      </span>
                    </div>

                    <div>
                      <span className="text-gray-600">Méthode paiement:</span>
                      <span className="ml-2">{selectedOrder.paymentMethod || "-"}</span>
                    </div>

                    <div>
                      <span className="text-gray-600">Statut paiement:</span>
                      <span className="ml-2">{getStatusBadge(selectedOrder.paymentStatus)}</span>
                    </div>

                    <div>
                      <span className="text-gray-600">Statut commande:</span>
                      <span className="ml-2">{selectedOrder.status || "-"}</span>
                    </div>

                    <div className="sm:col-span-2">
                      <span className="text-gray-600">Total:</span>
                      <span className="ml-2 font-bold text-green-600 text-base sm:text-lg">
                        {selectedOrder.total || 0} MAD
                      </span>
                    </div>
                  </div>
                </div>

                {/* Client - RESPONSIVE */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm sm:text-base">Client</h3>
                  <div className="text-xs sm:text-sm space-y-1">
                    <p>
                      <span className="text-gray-600">Nom:</span>{" "}
                      <span className="font-medium">
                        {selectedOrder.userId?.firstName || ""} {selectedOrder.userId?.lastName || ""}
                      </span>
                    </p>
                    <p className="break-words">
                      <span className="text-gray-600">Email:</span>{" "}
                      <span className="break-all">{selectedOrder.userId?.email || "-"}</span>
                    </p>
                    <p>
                      <span className="text-gray-600">Téléphone:</span>{" "}
                      <a
                        href={`tel:${selectedOrder.userId?.phone}`}
                        className="text-blue-600 hover:underline"
                      >
                        {selectedOrder.userId?.phone || "-"}
                      </a>
                    </p>
                  </div>
                </div>

                {/* Articles - RESPONSIVE */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <h3 className="font-semibold mb-2 text-sm sm:text-base">
                    Articles ({selectedOrder.items?.length || 0})
                  </h3>

                  <div className="space-y-3">
                    {selectedOrder.items?.length > 0 ? (
                      selectedOrder.items.map((item, index) => {
                        const dealDoc = item.dealId && typeof item.dealId === "object" ? item.dealId : null;
                        const dealId = dealDoc?._id || item.dealId || item.deal || "";
                        const dealSlug = dealDoc?.slug || null;
                        const dealTitle = dealDoc?.title || item.title || item.dealTitle || "Deal";

                        const dealPath = dealSlug ? `/deals/${dealSlug}` : dealId ? `/deals/${dealId}` : "";

                        return (
                          <div
                            key={`${dealId || "item"}-${index}`}
                            className="flex flex-col sm:flex-row sm:justify-between text-xs sm:text-sm border-b pb-2 gap-2"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium break-words">
                                {dealPath ? (
                                  <Link
                                    to={dealPath}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:underline"
                                  >
                                    {dealTitle}
                                  </Link>
                                ) : (
                                  dealTitle
                                )}
                              </p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600 mt-1">
                                <span>Qté: {item.quantity || 0}</span>
                                {item.price != null && (
                                  <span>Prix unitaire: {Number(item.price) || 0} MAD</span>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <p className="font-bold text-green-600">
                                {(Number(item.price) || 0) * (Number(item.quantity) || 1)} MAD
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-600 text-sm">Aucun article</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}